import type {
  GroupRecord,
  StreamAnalytics,
  StreamRecord,
} from '@vlabs/api/artemy/model';

import type { StreamAnalyticsRecordItem } from '../records';
import { normalizeStreamAnalyticsRecordItem } from '../records';

const ROI_SKIP_KEYS = ['roi', 'droi'];

// Личные обязательные поля потока (не наследуются от группы) — в sync-сравнении не участвуют
const SETTINGS_SKIP_KEYS = ['type', 'reference'];

export type UnsyncedParamKind = 'settings' | 'analytics';

export interface UnsyncedParam {
  kind: UnsyncedParamKind;
  /** Путь к параметру: 'data.reference' или 'analytics:facestream1.parameters.mode' */
  path: string;
  analyticsType?: string;
  analyticsLabel?: string;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Перечисляет пути листовых ключей объекта рекурсивно (deep).
 * Массивы и пустые объекты считаются листьями. Используется для
 * пофилдовых sync-статусов параметров ВА.
 */
export function collectObjectPaths(
  value: unknown,
  skipKeys: readonly string[] = [],
): string[] {
  if (!isPlainObject(value)) return [];

  const keys: string[] = [];

  Object.entries(value).forEach(([key, child]) => {
    if (skipKeys.includes(key)) return;

    if (isPlainObject(child) && Object.keys(child).length > 0) {
      collectObjectPaths(child, skipKeys).forEach((childKey) => {
        keys.push(`${key}.${childKey}`);
      });
    } else {
      keys.push(key);
    }
  });

  return keys;
}

// Собирает пути ключей группового объекта, которые есть в личном объекте потока.
// shallow: первый присутствующий ключ считается рассинхронизированным блоком, глубже не идём.
// deep: рекурсивно перечисляем все пересекающиеся ключи.
function collectUnsyncedKeys(
  groupValue: unknown,
  streamValue: unknown,
  skipKeys: readonly string[] = [],
  deep = false,
): string[] {
  if (!isPlainObject(groupValue)) return [];

  const streamObject = isPlainObject(streamValue) ? streamValue : {};
  const keys: string[] = [];

  Object.entries(groupValue).forEach(([key, groupChild]) => {
    if (skipKeys.includes(key)) return;

    if (key in streamObject) {
      keys.push(key);

      if (deep && isPlainObject(groupChild)) {
        collectUnsyncedKeys(groupChild, streamObject[key], skipKeys, deep).forEach((childKey) => {
          keys.push(`${key}.${childKey}`);
        });
      }
    } else if (isPlainObject(groupChild)) {
      collectUnsyncedKeys(groupChild, streamObject[key], skipKeys, deep).forEach((childKey) => {
        keys.push(`${key}.${childKey}`);
      });
    }
  });

  return keys;
}

function getAnalyticsKey(analytics: StreamAnalytics | StreamAnalyticsRecordItem): string | undefined {
  const label = typeof analytics.label === 'string' ? analytics.label : undefined;
  const type = typeof analytics.type === 'string' ? analytics.type : undefined;

  return label ?? type;
}

function getAnalyticsHandlerId(item: unknown): string | undefined {
  const handlerId = (item as {
    parameters?: { parameters?: { event_handler?: { handler_id?: unknown } } };
  })?.parameters?.parameters?.event_handler?.handler_id;

  return typeof handlerId === 'string' ? handlerId : undefined;
}

/**
 * Ищет в списке аналитик запись, соответствующую элементу ВА (личной записи потока
 * или элементу enriched-композита). Цепочка матчинга: ключ (label ?? type) →
 * event_handler.handler_id. Фолбэк по handler_id нужен, потому что бэк генерирует
 * label (напр. analytic_0) для ВА без label в enriched-ответе; handler_id
 * сохраняется в личной копии ВА, поэтому по нему личная ВА однозначно
 * соответствует групповой. Совпадение только по type не считаем матчем:
 * это может быть отдельная личная ВА потока без аналога в группе.
 */
export function findGroupAnalyticsItem(
  groupAnalytics: StreamAnalytics[] | undefined,
  item: StreamAnalytics | StreamAnalyticsRecordItem,
): StreamAnalytics | undefined {
  if (!groupAnalytics?.length) return undefined;

  const key = getAnalyticsKey(item);
  if (key) {
    const byKey = groupAnalytics.find((groupItem) => getAnalyticsKey(groupItem) === key);
    if (byKey) return byKey;
  }

  const handlerId = getAnalyticsHandlerId(item);
  if (!handlerId) return undefined;

  return groupAnalytics.find((groupItem) => getAnalyticsHandlerId(groupItem) === handlerId);
}

// Ищет личную ВА потока, соответствующую групповой: ключ (label ?? type) →
// handler_id. Личные записи должны быть нормализованы (analytic_name → type).
function findStreamAnalytics(
  personalItems: StreamAnalyticsRecordItem[],
  groupAnalytics: StreamAnalytics,
): StreamAnalyticsRecordItem | undefined {
  return findGroupAnalyticsItem(
    personalItems as unknown as StreamAnalytics[],
    groupAnalytics,
  );
}

/**
 * Сравнивает групповые параметры и личные параметры потока (полученного с `enriched: 0`).
 * Параметр группы, указанный в личных параметрах потока, считается не синхронизированным.
 * ROI/DROI не проверяются.
 */
export function checkStreamSync(
  group: GroupRecord,
  stream: StreamRecord,
): UnsyncedParam[] {
  const unsynced: UnsyncedParam[] = [];

  collectUnsyncedKeys(
    group.stream_settings?.data,
    stream.data,
    SETTINGS_SKIP_KEYS,
    true,
  ).forEach((key) => {
    unsynced.push({ kind: 'settings', path: `data.${key}` });
  });

  collectUnsyncedKeys(group.stream_settings?.autorestart, stream.autorestart).forEach((key) => {
    unsynced.push({ kind: 'settings', path: `autorestart.${key}` });
  });

  collectUnsyncedKeys(group.stream_settings?.location, stream.location).forEach((key) => {
    unsynced.push({ kind: 'settings', path: `location.${key}` });
  });

  // Личные записи ВА (enriched=0 может отдавать analytic_name вместо type — нормализуем).
  // Цепочка матчинга к групповой ВА: ключ (label ?? type) → handler_id.
  const personalItems = (stream.analytics ?? [])
    .map((item) => normalizeStreamAnalyticsRecordItem(item));

  (group.analytics ?? []).forEach((groupAnalytics) => {
    const key = getAnalyticsKey(groupAnalytics);
    if (!key) return;

    const streamAnalytics = findStreamAnalytics(personalItems, groupAnalytics);
    if (!streamAnalytics) return;

    unsynced.push({
      kind: 'analytics',
      path: `analytics:${key}`,
      analyticsType: groupAnalytics.type,
      analyticsLabel: groupAnalytics.label,
    });

    collectUnsyncedKeys(
      groupAnalytics.parameters,
      streamAnalytics.parameters,
      ROI_SKIP_KEYS,
      true,
    ).forEach((paramKey) => {
      unsynced.push({
        kind: 'analytics',
        path: `analytics:${key}.parameters.${paramKey}`,
        analyticsType: groupAnalytics.type,
        analyticsLabel: groupAnalytics.label,
      });
    });
  });

  return unsynced;
}

export function hasUnsyncedParams(group: GroupRecord, stream: StreamRecord): boolean {
  return checkStreamSync(group, stream).length > 0;
}
